import type { Dispatch, ReactNode, SetStateAction } from 'react';
import { useEffect, useRef, useState } from 'react';

import { useLazyQuery } from '@apollo/client/react';
import {
  Box,
  CircularProgress,
  Collapse,
  Link,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { InfoIcon } from 'lucide-react';

import { gql } from '@graphql/__generated__';
import { TenantSelect } from '@modules/_shared/components/tenant-select/TenantSelect';
import { UserSelect } from '@modules/_shared/components/user-select/UserSelect';
import { GET_CLOSE_LEAD } from '@modules/organisations/organisationsQueries';
import {
  MatchSuggestionField,
  type SourceMatch,
} from '@modules/teams/components/MatchSuggestionField';
import { useLocale } from '@shared/hooks/locale';
import { customPalette } from '@shared/theme/StyleProvider';

const MATCH_TENANT_BY_NAME = gql(/* GraphQL */ `
  query MatchTenantByName($name: String!) {
    read_only_tenants(where: { name: { _ilike: $name } }, limit: 2) {
      id
      name
    }
  }
`);

/**
 * An org/team email plus a human label (the org name or a team member's name).
 * Drives the Stripe/Close email matching and the validation reference list.
 */
export type KnownContact = {
  name: string | null;
  email: string;
};

/**
 * Collapsible reference list of the org's known emails (org + team members) so
 * the user can eyeball-validate a Stripe/Close suggestion: a card whose email
 * appears here is the same person/company. Cards flag the match themselves; this
 * just exposes the full roster behind a toggle to keep the section compact.
 */
const KnownContactsReference = ({
  contacts,
}: {
  contacts: ReadonlyArray<KnownContact>;
}) => {
  const { t } = useLocale();
  const [open, setOpen] = useState(false);
  return (
    <Box>
      <Link
        component="button"
        type="button"
        variant="caption"
        underline="hover"
        onClick={() => setOpen(value => !value)}
        sx={{ fontWeight: 600 }}
      >
        {open
          ? t('Hide team contacts')
          : t('Show team contacts ({{count}})', { count: contacts.length })}
      </Link>
      <Collapse in={open} unmountOnExit>
        <Stack spacing={0.25} sx={{ mt: 0.5 }}>
          {contacts.map(contact => (
            <Typography
              key={contact.email}
              variant="caption"
              color="text.secondary"
              noWrap
            >
              {contact.name != null
                ? `${contact.name} · ${contact.email}`
                : contact.email}
            </Typography>
          ))}
        </Stack>
      </Collapse>
    </Box>
  );
};

/** Section header styled to match RaForm's `category-title`. */
const SectionTitle = ({
  children,
  tooltip,
}: {
  children: ReactNode;
  tooltip?: string;
}) => (
  <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', pb: 0.75 }}>
    <Stack direction="row" sx={{ alignItems: 'center', gap: 0.5 }}>
      <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 600 }}>
        {children}
      </Typography>
      {tooltip != null && (
        <Tooltip title={tooltip} arrow>
          <InfoIcon size={16} color={customPalette.gray400} />
        </Tooltip>
      )}
    </Stack>
  </Box>
);

export const FieldLabel = ({
  children,
  loading = false,
}: {
  children: ReactNode;
  loading?: boolean;
}) => (
  <Stack direction="row" sx={{ alignItems: 'center', gap: 1, mb: 1 }}>
    <Typography variant="subtitle2">{children}</Typography>
    {loading && <CircularProgress size={12} />}
  </Stack>
);

const assignmentGridSx = {
  display: 'grid',
  // `minmax(0, 1fr)` (not `1fr`) so columns can shrink below their content's
  // intrinsic width — otherwise long unbreakable ids (Stripe/Close) blow the
  // track past the drawer width.
  gridTemplateColumns: {
    xs: 'minmax(0, 1fr)',
    sm: 'minmax(0, 1fr) minmax(0, 1fr)',
  },
  gap: 2,
} as const;

export type OrganisationAccountFieldsProps = {
  /** Names used to auto-search Stripe / Close matches. */
  suggestedQueries: string[];
  hasStripeKey: boolean;
  hasCloseKey: boolean;
  stripeMatches: SourceMatch[];
  onAddStripe: (match: SourceMatch) => void;
  onRemoveStripe: (id: string) => void;
  closeMatch: SourceMatch | null;
  onSelectClose: (match: SourceMatch | null) => void;
  salesRepUserId: string | null;
  setSalesRepUserId: Dispatch<SetStateAction<string | null>>;
  csmRepUserId: string | null;
  setCsmRepUserId: Dispatch<SetStateAction<string | null>>;
  linkedTenantId: string | null;
  setLinkedTenantId: Dispatch<SetStateAction<string | null>>;
  /**
   * When provided, the (empty) linked tenant is prefilled by matching this
   * name against existing CRM tenants. Used on organisation/team creation.
   */
  tenantMatchName?: string;
  /**
   * The org being edited (absent on creation). Forwarded to the Stripe/Close
   * search so matches already linked to *other* orgs are filtered out.
   */
  organisationId?: string;
  /**
   * The org's + its team members' emails. A Stripe customer whose email matches
   * one of these is recommended outright (the billing account belongs to that
   * person) even when the org name never surfaces it.
   */
  knownEmails?: ReadonlyArray<string | null | undefined>;
  /**
   * The same emails as `knownEmails` but paired with a name (org or team
   * member). Rendered as a collapsible list so the user can eyeball-validate a
   * Stripe/Close suggestion against the people who belong to this org.
   */
  knownContacts?: ReadonlyArray<KnownContact>;
  /** Stripe customer ids already persisted on the org (styled as "Set"). */
  savedStripeIds?: ReadonlyArray<string>;
  /** Close lead id already persisted on the org (styled as "Set"). */
  savedCloseId?: string | null;
};

/**
 * Shared "account" section of the organisation create/update form: suggested
 * external-system matches (Stripe / Close), sales & CSM assignments, and the
 * linked CRM tenant. Reused by the organisation create drawer, settings, and
 * the create-team flow so all three stay visually and behaviourally identical.
 */
export const OrganisationAccountFields = ({
  suggestedQueries,
  hasStripeKey,
  hasCloseKey,
  stripeMatches,
  onAddStripe,
  onRemoveStripe,
  closeMatch,
  onSelectClose,
  salesRepUserId,
  setSalesRepUserId,
  csmRepUserId,
  setCsmRepUserId,
  linkedTenantId,
  setLinkedTenantId,
  tenantMatchName,
  organisationId,
  knownEmails,
  knownContacts,
  savedStripeIds,
  savedCloseId,
}: OrganisationAccountFieldsProps) => {
  const { t } = useLocale();
  const [getCloseLead] = useLazyQuery(GET_CLOSE_LEAD);
  const [matchTenantByName] = useLazyQuery(MATCH_TENANT_BY_NAME);
  const closePrefilledLeadId = useRef<string | null>(null);
  const tenantPrefillDone = useRef(false);
  const showMatches = hasStripeKey || hasCloseKey;

  // Dedicated prefill flag (decoupled from the lazy-query loading state so the
  // spinner is reliable across the effect's re-runs).
  const [prefillingAssignments, setPrefillingAssignments] = useState(false);

  // Mirror the latest assignment values into refs so the prefill effect can
  // read them without re-running (and re-fetching) every time they change.
  const salesRepRef = useRef(salesRepUserId);
  const csmRepRef = useRef(csmRepUserId);
  const linkedTenantRef = useRef(linkedTenantId);
  salesRepRef.current = salesRepUserId;
  csmRepRef.current = csmRepUserId;
  linkedTenantRef.current = linkedTenantId;

  // Show the loading state only on the empty field(s) we are about to fill.
  const salesRepPrefilling = prefillingAssignments && salesRepUserId == null;
  const csmRepPrefilling = prefillingAssignments && csmRepUserId == null;

  // Prefill the sales rep / CSM from the selected Close lead — only when at
  // least one of them is still empty, so we never overwrite existing values
  // nor fire a needless request when both are already set.
  useEffect(() => {
    if (closeMatch == null) {
      return;
    }
    if (closePrefilledLeadId.current === closeMatch.id) {
      return;
    }
    if (salesRepRef.current != null && csmRepRef.current != null) {
      return;
    }
    closePrefilledLeadId.current = closeMatch.id;

    let active = true;
    setPrefillingAssignments(true);
    getCloseLead({ variables: { leadId: closeMatch.id } })
      .then(response => {
        const lead = response.data?.get_close_lead;
        if (!active || lead == null) {
          return;
        }
        if (lead.sales_rep_user_id != null) {
          setSalesRepUserId(
            current => current ?? lead.sales_rep_user_id ?? null,
          );
        }
        if (lead.csm_rep_user_id != null) {
          setCsmRepUserId(current => current ?? lead.csm_rep_user_id ?? null);
        }
      })
      .finally(() => {
        if (active) {
          setPrefillingAssignments(false);
        }
      });

    return () => {
      active = false;
    };
  }, [closeMatch, getCloseLead, setSalesRepUserId, setCsmRepUserId]);

  // Prefill the linked tenant by matching its name — only when empty.
  useEffect(() => {
    if (tenantMatchName == null || tenantPrefillDone.current) {
      return;
    }
    if (linkedTenantRef.current != null) {
      return;
    }
    const name = tenantMatchName.trim();
    if (name === '') {
      return;
    }
    tenantPrefillDone.current = true;

    let active = true;
    matchTenantByName({ variables: { name } }).then(response => {
      const tenants = response.data?.read_only_tenants ?? [];
      if (!active || tenants.length === 0) {
        return;
      }
      const lowerName = name.toLowerCase();
      const match =
        tenants.find(
          tenant => (tenant.name ?? '').toLowerCase() === lowerName,
        ) ?? (tenants.length === 1 ? tenants[0] : null);
      const matchId = match?.id;
      if (matchId != null) {
        setLinkedTenantId(current => current ?? matchId);
      }
    });

    return () => {
      active = false;
    };
  }, [tenantMatchName, matchTenantByName, setLinkedTenantId]);

  return (
    <Stack spacing={3}>
      {showMatches && (
        <Stack spacing={1.5}>
          <Stack spacing={0.25}>
            <SectionTitle
              tooltip={t(
                'Matches are searched using the organisation and team names.',
              )}
            >
              {t('Suggested matches')}
            </SectionTitle>
            <Typography variant="caption" color="text.secondary">
              {t('We found likely matches for this organisation.')}
            </Typography>
          </Stack>
          {knownContacts != null && knownContacts.length > 0 && (
            <KnownContactsReference contacts={knownContacts} />
          )}
          <Box sx={{ ...assignmentGridSx, alignItems: 'start' }}>
            {hasStripeKey && (
              <MatchSuggestionField
                source="stripe"
                label={t('Stripe customer')}
                searchPlaceholder={t('Search by name, email, or customer ID')}
                suggestedQueries={suggestedQueries}
                selected={stripeMatches}
                onAdd={onAddStripe}
                onRemove={onRemoveStripe}
                organisationId={organisationId}
                knownEmails={knownEmails}
                savedIds={savedStripeIds}
                multiple
              />
            )}
            {hasCloseKey && (
              <MatchSuggestionField
                source="close"
                label={t('Close CRM lead')}
                searchPlaceholder={t('Search Close lead')}
                suggestedQueries={suggestedQueries}
                selected={closeMatch != null ? [closeMatch] : []}
                onAdd={match => onSelectClose(match)}
                onRemove={() => onSelectClose(null)}
                organisationId={organisationId}
                knownEmails={knownEmails}
                savedIds={savedCloseId != null ? [savedCloseId] : undefined}
              />
            )}
          </Box>
        </Stack>
      )}

      <Stack spacing={1.5}>
        <SectionTitle>{t('Assignments')}</SectionTitle>
        <Box sx={assignmentGridSx}>
          <Box>
            <FieldLabel loading={salesRepPrefilling}>
              {t('Sales rep')}
            </FieldLabel>
            {salesRepPrefilling ? (
              <Skeleton variant="rounded" height={40} />
            ) : (
              <UserSelect
                userId={salesRepUserId}
                onChange={userId => setSalesRepUserId(userId)}
                InputProps={{ placeholder: t('Select sales rep') }}
              />
            )}
          </Box>
          <Box>
            <FieldLabel loading={csmRepPrefilling}>{t('CSM rep')}</FieldLabel>
            {csmRepPrefilling ? (
              <Skeleton variant="rounded" height={40} />
            ) : (
              <UserSelect
                userId={csmRepUserId}
                onChange={userId => setCsmRepUserId(userId)}
                InputProps={{ placeholder: t('Select CSM rep') }}
              />
            )}
          </Box>
        </Box>
      </Stack>

      <Stack spacing={1.5}>
        <SectionTitle>{t('CRM')}</SectionTitle>
        <Box sx={assignmentGridSx}>
          <Box>
            <FieldLabel>{t('Linked CRM tenant')}</FieldLabel>
            <TenantSelect
              tenantId={linkedTenantId}
              onChange={setLinkedTenantId}
              InputProps={{ placeholder: t('Search linked CRM tenant') }}
            />
          </Box>
        </Box>
      </Stack>
    </Stack>
  );
};
